'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type Clip } from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { AssetPreview } from '~/components/AssetPreview';
import { AssetMetadataPanel } from '~/components/asset-detail/AssetMetadataPanel';
import { AssetOptionsSection } from '~/components/asset-detail/AssetOptionsSection';
import { type VersionReference } from '~/components/asset-detail/VersionReferenceRow';
import { Button, type ButtonProps } from '~/components/Button';
import { VersionSelector } from '~/components/VersionSelector';
import { useAssetVersionsList } from '~/hooks/api/useAssetVersionsList';

export type AssetInsertOptions = {
  width: number;
  height: number;
  versionReference: VersionReference;
  /** CDN link URL when versionReference is 'dynamic' */
  dynamicUrl?: string;
};

export type AssetDetailPanelProps = {
  /** The clip to display */
  clip?: Clip;
  /** The workspace ID for fetching versions */
  workspaceId: string;
  /** Called when the user clicks the insert button with the active clip and image options */
  onInsert: (clip: Clip, imageOptions: AssetInsertOptions) => void;
  /** Called when the user clicks cancel */
  onClose: () => void;
  /** Called when user switches to a different version */
  onVersionChange?: (clip: Clip) => void;
  /** Show loading skeleton while clip is being fetched */
  isLoading?: boolean;
  /** Label for the insert button (defaults to "Insert") */
  insertButtonLabel?: string;
  /** Additional CSS classes for the root element */
  className?: string;
  /** Whether to show the version reference toggle. Consumers typically derive this from the cdnLinks workspace add-on */
  showVersionReference?: boolean;
  /** Initial image options to restore previous selections when reopening the panel */
  initialAssetOptions?: Partial<AssetInsertOptions>;
  /** Override the section header inside the embed options block (defaults to "Image Options" / "Video Options"). */
  optionsSectionLabel?: string;
  /** Button size for the Generate CDN modal CTAs. Forwarded to `AssetOptionsSection` → `useCdnLink`. */
  cdnLinkModalButtonSize?: ButtonProps['size'];
  /**
   * When true, selecting Dynamic version reference forces the active version to latest
   * and disables the version selector. Opt-in per-consumer; defaults to false.
   */
  forceLatestOnDynamic?: boolean;
};

const MetadataPanelSkeleton = memo(() => (
  <div className="flex flex-col gap-4">
    {/* Title skeleton */}
    <div>
      <div className="h-5 w-3/4 animate-pulse rounded bg-neutral-4" />
      <div className="mt-1 h-4 w-1/2 animate-pulse rounded bg-neutral-4" />
    </div>
    {/* File info skeleton */}
    <div className="space-y-2">
      <div className="h-3 w-16 animate-pulse rounded bg-neutral-4" />
      <div className="h-4 w-full animate-pulse rounded bg-neutral-4" />
      <div className="h-4 w-2/3 animate-pulse rounded bg-neutral-4" />
    </div>
  </div>
));

MetadataPanelSkeleton.displayName = 'MetadataPanelSkeleton';

const PreviewSkeleton = memo(() => (
  <div className="flex size-full items-center justify-center">
    <div className="size-64 animate-pulse rounded-8 bg-neutral-4" />
  </div>
));

PreviewSkeleton.displayName = 'PreviewSkeleton';

/**
 * Full-size panel for viewing asset details before insertion.
 * Replaces the gallery content when an asset is selected.
 * Shows a preview with zoom controls and version selector, a metadata sidebar,
 * and an insert/cancel footer.
 */
export const AssetDetailPanel = memo(
  ({
    clip,
    workspaceId,
    onInsert,
    onClose,
    onVersionChange,
    isLoading,
    insertButtonLabel = 'Insert',
    className,
    showVersionReference = false,
    initialAssetOptions,
    optionsSectionLabel,
    cdnLinkModalButtonSize,
    forceLatestOnDynamic = false,
  }: AssetDetailPanelProps) => {
    const [versionOverride, setVersionOverride] = useState<Clip | undefined>();
    const [versionReference, setVersionReference] = useState<VersionReference>(
      initialAssetOptions?.versionReference ?? 'static',
    );
    const activeClip = versionOverride ?? clip;
    const insertOptionsRef = useRef<AssetInsertOptions>({
      dynamicUrl: undefined,
      height: initialAssetOptions?.height ?? clip?.darHeight ?? clip?.height ?? 0,
      versionReference: initialAssetOptions?.versionReference ?? 'static',
      width: initialAssetOptions?.width ?? clip?.darWidth ?? clip?.width ?? 0,
    });

    const isVersionSelectorDisabled = forceLatestOnDynamic && versionReference === 'dynamic';

    const { data: versionsData } = useAssetVersionsList({
      assetId: clip?.assetId,
      workspaceId,
    });

    const versions = useMemo(
      () => versionsData?.pages.flatMap((page) => page.data.clips).filter(Boolean) ?? [],
      [versionsData?.pages],
    );

    const latestVersion = useMemo(
      () => (versions.length > 0 ? versions.reduce((a, b) => (a.version > b.version ? a : b)) : undefined),
      [versions],
    );

    const handleVersionSelect = useCallback(
      (selectedClip: Clip) => {
        setVersionOverride(selectedClip);
        onVersionChange?.(selectedClip);
      },
      [onVersionChange],
    );

    useEffect(() => {
      if (isVersionSelectorDisabled && latestVersion && activeClip && activeClip.version !== latestVersion.version) {
        setVersionOverride(latestVersion);
      }
    }, [isVersionSelectorDisabled, latestVersion, activeClip]);

    const handleInsert = useCallback(() => {
      if (!activeClip) {
        return;
      }

      onInsert(activeClip, insertOptionsRef.current);
    }, [activeClip, onInsert]);

    const handleOptionsChange = useCallback((options: AssetInsertOptions) => {
      insertOptionsRef.current = options;
    }, []);

    return (
      <div className={cn('absolute inset-0 z-50 flex flex-col overflow-hidden bg-white dark:bg-neutral-1', className)}>
        <div className="flex min-h-0 flex-1">
          <div className="relative flex min-w-0 flex-1 items-center justify-center overflow-hidden bg-neutral-2 px-2 dark:bg-neutral-3">
            {isLoading || !activeClip ? <PreviewSkeleton /> : <AssetPreview clip={activeClip} />}

            {activeClip && versions.length > 1 && (
              <div className="absolute top-6 left-6 z-10">
                <VersionSelector
                  disabled={isVersionSelectorDisabled}
                  disabledTooltip="Switch to Static to pick a specific version"
                  onVersionSelect={handleVersionSelect}
                  selectedVersion={activeClip.version}
                  versions={versions}
                />
              </div>
            )}
          </div>

          <div className="flex w-[328px] shrink-0 flex-col overflow-hidden border-neutral-4 border-l">
            <div className="flex-1 overflow-y-auto p-4">
              {isLoading || !activeClip ? <MetadataPanelSkeleton /> : <AssetMetadataPanel clip={activeClip} />}
            </div>

            {activeClip && (
              <AssetOptionsSection
                activeClip={activeClip}
                cdnLinkModalButtonSize={cdnLinkModalButtonSize}
                initialAssetOptions={initialAssetOptions}
                onOptionsChange={handleOptionsChange}
                onVersionReferenceChange={setVersionReference}
                sectionLabel={optionsSectionLabel}
                showVersionReference={showVersionReference}
                versionReference={versionReference}
                workspaceId={workspaceId}
              />
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 border-neutral-4 border-t px-4 py-3">
          <Button appearance="outline" onClick={onClose} size="medium">
            Cancel
          </Button>
          <Button appearance="primary" disabled={isLoading || !activeClip} onClick={handleInsert} size="medium">
            {insertButtonLabel}
          </Button>
        </div>
      </div>
    );
  },
);

AssetDetailPanel.displayName = 'AssetDetailPanel';
